<template>
  <div class="settings-view">
    <RouterLink class="app-link settings-back" to="/settings/projects">
      <ChevronLeft class="icon" />
      Projects
    </RouterLink>
    <PageHeader title="Project access" :subtitle="subtitle" />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!access">
      <div class="access-card">
        <div class="access-tools">
          <div class="settings-search">
            <Search class="icon" />
            <input v-model="search" type="search" placeholder="Search members by name or email" />
          </div>
          <span class="access-count">{{ access?.users?.length ?? 0 }} members</span>
        </div>
        <div class="admin-table-wrap access-table-wrap">
          <table class="admin-table access-matrix">
            <thead>
              <tr>
                <th class="access-user-col">Member</th>
                <th v-for="role in access?.roles ?? []" :key="role.roleId" class="access-role-col">{{ role.roleName }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in filteredUsers" :key="user.userId">
                <td class="access-user-col">
                  <span class="user-cell">
                    <UserAvatar :name="user.userName" size="medium" aria-hidden="true" />
                    <span class="access-user-text">
                      <strong>{{ user.userName }}</strong>
                      <span class="access-user-mail">{{ user.userEmail }}</span>
                    </span>
                  </span>
                </td>
                <td
                  v-for="role in access?.roles ?? []"
                  :key="role.roleId"
                  class="access-cell"
                  :class="{ changed: isChanged(user.userId, role.roleId) }"
                >
                  <ToggleSwitch
                    :model-value="isAssigned(user.userId, role.roleId)"
                    @update:model-value="value => setAssignment(user.userId, role.roleId, value)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="filteredUsers.length === 0" class="settings-state">
            <SearchX class="icon" />
            <span>No members match “{{ search }}”.</span>
          </div>
        </div>
        <div class="settings-save-bar">
          <span class="settings-save-hint" :class="{ dirty: pendingCount > 0 }">
            {{ pendingCount > 0 ? `${pendingCount} pending change${pendingCount > 1 ? 's' : ''}` : 'All changes saved' }}
          </span>
          <button type="button" class="btn" :disabled="pendingCount === 0" @click="discard">Discard</button>
          <button type="button" class="btn btn-primary" :disabled="pendingCount === 0" @click="save">Save access</button>
        </div>
      </div>
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { ChevronLeft, Search, SearchX } from 'lucide-vue-next';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import type { EntityId } from '../api/entityId';
import {
  CommandsService,
  type GetProjectAccessRulesQueryResponse,
  type ProjectRoleAssignmentResponse,
  type ProjectRoleAssignmentChange
} from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import ToggleSwitch from '../components/settings/ToggleSwitch.vue';
import UserAvatar from '../components/UserAvatar.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';

const route = useRoute();
const session = useSessionStore();
const access = ref<GetProjectAccessRulesQueryResponse | null>(null);
const search = ref('');
const message = ref<string | null>(null);
const selectedAssignments = reactive<Record<EntityId, Set<EntityId>>>({});
const originalAssignments = ref<Set<string>>(new Set<string>());

const subtitle = computed(() =>
  access.value?.projectName ? `${access.value.projectName} · assign roles per member` : undefined);
const filteredUsers = computed(() => {
  const term = search.value.trim().toLowerCase();
  const users = access.value?.users ?? [];
  if (!term) {
    return users;
  }

  return users.filter(user => `${user.userName} ${user.userEmail}`.toLowerCase().includes(term));
});
const pendingCount = computed(() => {
  let count = 0;
  for (const user of access.value?.users ?? []) {
    for (const role of access.value?.roles ?? []) {
      if (isChanged(user.userId, role.roleId)) {
        count++;
      }
    }
  }

  return count;
});

onMounted(load);
watch(() => route.params.projectId, load);

async function load(): Promise<void> {
  const projectId = getProjectId();
  try {
    const response = session.pageBootstrap?.projectAccess ?? null;
    if (response == null || String(response.projectId) !== projectId) {
      throw new Error('Project access bootstrap payload was missing.');
    }

    access.value = response;
    applyAssignments(response);
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load access rules.');
  }
}

async function save(): Promise<void> {
  if (!access.value) {
    return;
  }

  try {
    const projectId = getProjectId();
    const changes: ProjectRoleAssignmentChange[] = [];
    for (const user of access.value.users ?? []) {
      for (const role of access.value.roles ?? []) {
        const assigned = isAssigned(user.userId, role.roleId);
        const originallyAssigned = originalAssignments.value.has(assignmentKey(user.userId, role.roleId));
        if (assigned === originallyAssigned) {
          continue;
        }

        changes.push({ userId: user.userId, roleId: role.roleId, assigned });
      }
    }

    await CommandsService.commandSetProjectAccessRulesCommand({
      requestBody: { projectId, changes }
    });
    commitAssignments();
    message.value = 'Access rules saved.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to save access rule.');
  }
}

function discard(): void {
  if (access.value) {
    applyAssignments(access.value);
  }
}

function commitAssignments(): void {
  if (!access.value) {
    return;
  }

  const assignments: ProjectRoleAssignmentResponse[] = [];
  for (const user of access.value.users ?? []) {
    for (const role of access.value.roles ?? []) {
      if (isAssigned(user.userId, role.roleId)) {
        assignments.push({ userId: user.userId, roleId: role.roleId });
      }
    }
  }

  access.value.assignments = assignments;
  originalAssignments.value = new Set(assignments.map(assignment => assignmentKey(assignment.userId, assignment.roleId)));
}

function applyAssignments(response: GetProjectAccessRulesQueryResponse): void {
  for (const key of Object.keys(selectedAssignments)) {
    delete selectedAssignments[key];
  }

  const original = new Set<string>();
  for (const assignment of response.assignments ?? []) {
    selectedAssignments[assignment.userId] ??= new Set<EntityId>();
    selectedAssignments[assignment.userId].add(assignment.roleId);
    original.add(assignmentKey(assignment.userId, assignment.roleId));
  }

  originalAssignments.value = original;
}

function isAssigned(userId: EntityId, roleId: EntityId): boolean {
  return selectedAssignments[userId]?.has(roleId) === true;
}

function isChanged(userId: EntityId, roleId: EntityId): boolean {
  return isAssigned(userId, roleId) !== originalAssignments.value.has(assignmentKey(userId, roleId));
}

function setAssignment(userId: EntityId, roleId: EntityId, assigned: boolean): void {
  selectedAssignments[userId] ??= new Set<EntityId>();
  if (assigned) {
    selectedAssignments[userId].add(roleId);
  } else {
    selectedAssignments[userId].delete(roleId);
  }
}

function assignmentKey(userId: EntityId, roleId: EntityId): string {
  return `${userId}:${roleId}`;
}

function getProjectId(): EntityId {
  const value = Array.isArray(route.params.projectId) ? route.params.projectId[0] : route.params.projectId;
  if (!value || !/^\d+$/.test(value)) {
    throw new Error('Project id is invalid.');
  }

  return value;
}
</script>
